import { Box } from "@mui/material";
import clsx from "clsx";
import type { JSONValue } from "../../../../models/fibo";
import { vglNumber } from "../../../../utils/vglTokens";
import { BBoxPoint, BoundingBoxInput, MIN_BBOX_SIZE } from "./BoundingBoxInput";
import styles from "./FiboPromptTree.module.scss";

// Coordinates may be model tokens ("<coord_33.2>") — parse before editing so the
// inputs get numbers and a write-back never re-serializes a token.
const parsePoint = (p: unknown): BBoxPoint | undefined => {
	const point = p as Record<string, unknown> | undefined;
	const x = vglNumber(point?.x);
	const y = vglNumber(point?.y);
	return x !== undefined && y !== undefined ? { x, y } : undefined;
};

interface BoundingBoxTreeNodeProps {
	v: JSONValue;
	path: string[];
	subtreeChanged: boolean;
	isPreserved: boolean;
	isEditable?: boolean;
	onValueChange?: (path: string[], value: JSONValue) => void;
}

export const BoundingBoxTreeNode = ({
	v,
	path,
	subtreeChanged,
	isPreserved,
	isEditable,
	onValueChange,
}: BoundingBoxTreeNodeProps) => {
	const bbox = v as unknown as Record<string, unknown>;
	const topLeftPoint = parsePoint(bbox.top_left);
	const bottomRightPoint = parsePoint(bbox.bottom_right);

	const handleChange = (label: "top_left" | "bottom_right", newValue: BBoxPoint) => {
		if (!onValueChange || !topLeftPoint || !bottomRightPoint) return;

		let topLeft = label === "top_left" ? newValue : { ...topLeftPoint };
		let bottomRight = label === "bottom_right" ? newValue : { ...bottomRightPoint };

		// Auto-adjust the other point to maintain minimum bbox size
		if (label === "top_left") {
			if (bottomRight.x <= newValue.x + MIN_BBOX_SIZE) bottomRight.x = Math.min(100, newValue.x + MIN_BBOX_SIZE);
			if (bottomRight.y <= newValue.y + MIN_BBOX_SIZE) bottomRight.y = Math.min(100, newValue.y + MIN_BBOX_SIZE);
		} else {
			if (topLeft.x >= newValue.x - MIN_BBOX_SIZE) topLeft.x = Math.max(0, newValue.x - MIN_BBOX_SIZE);
			if (topLeft.y >= newValue.y - MIN_BBOX_SIZE) topLeft.y = Math.max(0, newValue.y - MIN_BBOX_SIZE);
		}

		onValueChange(path, { ...bbox, top_left: topLeft, bottom_right: bottomRight } as unknown as JSONValue);
	};

	return (
		<Box
			className={clsx(styles.row, {
				[styles.isChanged]: subtreeChanged && !isPreserved,
				[styles.isPreserved]: isPreserved,
			})}
			onClick={(e) => e.stopPropagation()}
		>
			<Box className={styles.boundingBoxContainer}>
				{topLeftPoint && (
					<BoundingBoxInput
						label="top_left"
						value={topLeftPoint}
						disabled={!isEditable}
						onChange={handleChange}
					/>
				)}
				{bottomRightPoint && (
					<BoundingBoxInput
						label="bottom_right"
						value={bottomRightPoint}
						disabled={!isEditable}
						onChange={handleChange}
					/>
				)}
			</Box>
		</Box>
	);
};
